import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import GroupsIcon from '@mui/icons-material/Groups';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import PublicIcon from '@mui/icons-material/Public';
import { Box, Divider, Typography } from '@mui/material';
import ReactCountryFlag from 'react-country-flag';
import { SECURITY_FALLBACKS } from '../../../../../../corelogic/domain/constants/displayFallbacks.constants';
import { Season } from '../../../../../../corelogic/domain/models/season.model';
import { FrontendTournamentWithDetails } from '../../../../../../corelogic/domain/models/tournament.model';
import { formatDate } from '../../../../../../corelogic/domain/utils/date.utils';
import {
  calculateDemographics,
  isFMMAPublicTournament,
} from '../../../../../../corelogic/domain/utils/tournament.utils';
import { SafeText } from '../../../../../../corelogic/domain/utils/xssProtection.utils';
import PulsingActiveIcon from '../../Shared/PulsingActiveIcon/PulsingActiveIcon';

const styles = {
  container: {
    pt: 0,
    pl: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    bgcolor: 'rgba(20, 20, 20, 0.95)',
    backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7))',
    borderRadius: 2,
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.6)',
    border: '1px solid rgba(120, 120, 120, 0.3)',
    height: '100%',
    width: '100%',
    overflow: 'auto',
  },
  sectionTitle: {
    fontFamily: 'Rubik Mono One',
    fontSize: 'clamp(0.8rem, 1.2vw, 1rem)',
    color: '#cc0000',
    mt: 2,
    mb: 2,
    borderLeft: '3px solid #cc0000',
    pl: 1.5,
    py: 0.5,
    letterSpacing: '0.5px',
  },
  descriptionText: {
    color: '#ffffff',
    fontSize: '0.95rem',
    lineHeight: 1.6,
    px: 1,
    fontFamily: 'Arial',
  },
  detailItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    color: '#ffffff',
    py: 0.5,
  },
  detailIcon: {
    color: '#cc0000',
    fontSize: '1.2rem',
  },
  detailText: {
    color: '#ffffff',
    fontSize: '0.95rem',
    fontFamily: 'Arial',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: {
      xs: '1fr',
      sm: '1fr 1fr',
    },
    gap: 2,
    px: 1,
  },
  demographicsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
  },
  demographicCategory: {
    mt: 1.5,
    mb: 0.5,
    color: '#ffffff',
    fontWeight: 'bold',
    fontSize: '0.9rem',
    borderBottom: '1px solid rgba(204, 0, 0, 0.2)',
    pb: 0.5,
  },
  demographicItemsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 1,
    mt: 1,
  },
  statItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 12px',
    borderRadius: 1,
    minWidth: '120px',
    bgcolor: 'rgba(40, 40, 40, 0.7)',
    border: '1px solid rgba(60, 60, 60, 0.8)',
    transition: 'all 0.2s ease',
    '&:hover': {
      bgcolor: 'rgba(50, 50, 50, 0.7)',
      borderColor: 'rgba(204, 0, 0, 0.4)',
      transform: 'translateX(2px)',
    },
  },
  statItemContent: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  statValue: {
    color: '#ffffff',
    fontWeight: 'bold',
  },
  statLabel: {
    color: '#ffffff',
  },
  statPercentage: {
    color: '#aaaaaa',
    fontSize: '0.85rem',
  },
  countryFlag: {
    width: '20px',
    height: '14px',
    borderRadius: '2px',
    marginRight: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.4)',
  },
  noDataMessage: {
    color: '#aaaaaa',
    fontStyle: 'italic',
    textAlign: 'center',
    my: 2,
  },
  statusBadge: {
    ml: 1,
    px: 1,
    py: 0.2,
    borderRadius: 0.5,
    fontSize: '0.8rem',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
  },
  activeStatus: {
    bgcolor: 'rgba(0, 128, 0, 0.2)',
    color: '#00ff00',
    border: '1px solid rgba(0, 128, 0, 0.5)',
  },
  completedStatus: {
    bgcolor: 'rgba(128, 128, 128, 0.2)',
    color: '#cccccc',
    border: '1px solid rgba(128, 128, 128, 0.5)',
  },
  divider: {
    bgcolor: 'rgba(204, 0, 0, 0.1)',
    my: 1,
  },
};

interface DetailsTabProps {
  tournament: FrontendTournamentWithDetails;
  availableSeasons: Season[];
}

const DetailsTab = ({ tournament, availableSeasons }: DetailsTabProps) => {
  // Calculate demographics with util function
  const demographicsData = calculateDemographics(tournament);

  // Calculate if this is a FMMA public tournament
  const isFMMAPublic = isFMMAPublicTournament(
    tournament.name,
    tournament.creatorName,
    tournament.private,
  );

  return (
    <Box sx={styles.container}>
      {/* Description Section */}
      <Box>
        <Typography sx={styles.sectionTitle}>Description</Typography>
        <Typography variant="body2" sx={styles.descriptionText}>
          <SafeText
            text={
              tournament.description ||
              'No description provided for this tournament.'
            }
            fallback={SECURITY_FALLBACKS.TOURNAMENT_DESCRIPTION}
          />
        </Typography>
      </Box>

      <Divider sx={styles.divider} />

      {/* General Information Section */}
      <Box>
        <Typography sx={styles.sectionTitle}>General Information</Typography>

        <Box sx={styles.detailsGrid}>
          {/* Dates */}
          <Box sx={styles.detailItem}>
            <CalendarMonthIcon sx={styles.detailIcon} />
            <Typography variant="body2" sx={styles.detailText}>
              {formatDate(tournament.startDate)}
              {tournament.endDate
                ? ` - ${formatDate(tournament.endDate)}`
                : ' - TBC'}
            </Typography>
            <Box
              component="span"
              sx={{
                ...styles.statusBadge,
                ...(tournament.isActive
                  ? styles.activeStatus
                  : styles.completedStatus),
              }}
            >
              {tournament.isActive ? (
                <>
                  <PulsingActiveIcon /> Active{' '}
                </>
              ) : (
                <>
                  <CheckCircleIcon /> Completed
                </>
              )}
            </Box>
          </Box>

          {/* Participants */}
          <Box sx={styles.detailItem}>
            <GroupsIcon sx={styles.detailIcon} />
            <Typography variant="body2" sx={styles.detailText}>
              {isFMMAPublic
                ? 'Global'
                : tournament.participants === 0
                  ? 'No participants yet'
                  : `${tournament.participants} ${tournament.participants === 1 ? 'Participant' : 'Participants'}`}
            </Typography>
          </Box>

          {/* Seasons */}
          <Box sx={styles.detailItem}>
            <EmojiEventsIcon sx={styles.detailIcon} />
            <Typography variant="body2" sx={styles.detailText}>
              {availableSeasons.length === 0 && isFMMAPublic
                ? 'All Seasons in Calendar Year'
                : `${availableSeasons.length} of ${tournament.numberOfSeasons} Seasons`}
            </Typography>
          </Box>

          {/* Creator */}
          <Box sx={styles.detailItem}>
            <PersonIcon sx={styles.detailIcon} />
            <Typography variant="body2" sx={styles.detailText}>
              Created by:{' '}
              <SafeText
                text={isFMMAPublic ? 'Fantasy MMA' : tournament.creatorName}
                fallback={SECURITY_FALLBACKS.TOURNAMENT_CREATOR}
              />
            </Typography>
          </Box>

          {/* Status & Type */}
          <Box sx={styles.detailItem}>
            {tournament.private ? (
              <LockIcon sx={styles.detailIcon} />
            ) : (
              <PublicIcon sx={styles.detailIcon} />
            )}
            <Typography variant="body2" sx={styles.detailText}>
              {tournament.private ? 'Private Tournament' : 'Public Tournament'}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider sx={styles.divider} />

      {/* Demographics Section */}
      <Box>
        <Typography sx={styles.sectionTitle}>
          Participant Demographics
        </Typography>

        {demographicsData.hasData ? (
          <Box sx={styles.demographicsSection}>
            {/* Nationality Section */}
            <Box>
              <Typography variant="subtitle2" sx={styles.demographicCategory}>
                Top Countries
              </Typography>
              <Box sx={styles.demographicItemsContainer}>
                {demographicsData.nationalities.map((item) => (
                  <Box key={item.value} sx={styles.statItem}>
                    <Box sx={styles.statItemContent}>
                      {item.value !== 'other' && item.value !== 'unknown' && (
                        <ReactCountryFlag
                          countryCode={item.value}
                          svg
                          style={styles.countryFlag}
                          title={item.label}
                        />
                      )}
                    </Box>
                    <Typography variant="body2" sx={styles.statPercentage}>
                      {item.percentage}%
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Gender */}
            {/*<Box>
              <Typography variant="subtitle2" sx={styles.demographicCategory}>
                Gender Distribution
              </Typography>
              <Box sx={styles.demographicItemsContainer}>
                {demographicsData.genders.map((item) => (
                  <Box key={item.value} sx={styles.statItem}>
                    <Typography variant="body2" sx={styles.statLabel}>
                      {item.label}
                    </Typography>
                    <Typography variant="body2" sx={styles.statPercentage}>
                      {item.percentage}%
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            */}

            {/* Age */}
            {/*<Box>
              <Typography variant="subtitle2" sx={styles.demographicCategory}>
                Age Distribution
              </Typography>
              <Box sx={styles.demographicItemsContainer}>
                {demographicsData.ageGroups.map((item) => (
                  <Box key={item.value} sx={styles.statItem}>
                    <Typography variant="body2" sx={styles.statLabel}>
                      {item.label}
                    </Typography>
                    <Typography variant="body2" sx={styles.statPercentage}>
                      {item.percentage}%
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>*/}
          </Box>
        ) : (
          <Typography variant="body2" sx={styles.noDataMessage}>
            No participant data available
          </Typography>
        )}
      </Box>

      <Divider sx={styles.divider} />
    </Box>
  );
};

export default DetailsTab;
